import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Factory, Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { fmtDate } from "@/lib/format";
import { euro, Chip, SDI, PAY, DOC } from "@/lib/accounting";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InvoiceEditor } from "@/components/accounting/InvoiceEditor";
import { FromProductionDialog } from "@/components/accounting/FromProductionDialog";
import { InvoiceSheet } from "@/components/accounting/InvoiceSheet";

const XmlImport = ({ open, onClose, onDone }) => {
  const [files, setFiles] = useState([]);
  const [rep, setRep] = useState(null);
  const run = async () => {
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try { const { data } = await api.post("/accounting/import-xml", fd); setRep(data); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setRep(null); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="xml-import-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Importa fatture passive (XML SDI)</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Seleziona uno o più file XML o XML.P7M ricevuti dallo SDI. La società destinataria si riconosce dalla P.IVA; i fornitori mancanti vengono creati in anagrafica; i duplicati vengono saltati.</p>
        <Input type="file" multiple accept=".xml,.p7m" onChange={(e) => setFiles(e.target.files)} data-testid="xml-import-files" />
        <Button disabled={!files.length} onClick={run} data-testid="xml-import-submit">Importa</Button>
        {rep && <div className="border bg-muted/40 p-3 text-sm" data-testid="xml-import-report">{rep.imported} importate · {rep.duplicates} già presenti · {rep.suppliers_created} fornitori nuovi
          {rep.errors.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-red-700">{rep.errors.map((x, i) => <li key={i}>{x.file}: {x.error}</li>)}</ul>}</div>}
      </DialogContent>
    </Dialog>
  );
};

export const InvoicesTab = ({ direction }) => {
  const { can } = useAuth();
  const { companyId, nameOf } = useData();
  const [rows, setRows] = useState([]);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [q, setQ] = useState("");
  const [editor, setEditor] = useState(null);
  const [fromProd, setFromProd] = useState(false);
  const [xml, setXml] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => api.get("/accounting/invoices", { params: { direction, year, company_id: companyId || undefined } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [direction, year, companyId]);
  useEffect(() => { load(); }, [load]);
  const shown = rows.filter((r) => !q || `${r.number} ${r.party_name}`.toLowerCase().includes(q.toLowerCase()));
  const w = can("accounting:write");
  const p = direction === "OUT" ? "out" : "in";
  return (
    <div data-testid={`invoices-${p}-tab`}>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input className="w-28 bg-white" type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid={`invoices-${p}-year`} />
        <Input className="w-64 bg-white" placeholder="Cerca numero o controparte" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`invoices-${p}-search`} />
        <span className="text-sm text-muted-foreground">{shown.length} documenti · totale {euro(shown.reduce((s, r) => s + (r.doc_type === "TD04" ? -r.total : r.total), 0))}</span>
        {w && <div className="ml-auto flex gap-2">
          {direction === "OUT" && <Button variant="outline" onClick={() => setFromProd(true)} data-testid="invoice-from-production-button"><Factory className="mr-1 h-4 w-4" />Da produzione</Button>}
          {direction === "IN" && <Button variant="outline" onClick={() => setXml(true)} data-testid="invoice-import-xml-button"><Upload className="mr-1 h-4 w-4" />Importa XML</Button>}
          <Button onClick={() => setEditor({ invoice: null })} data-testid={`invoice-${p}-add-button`}><Plus className="mr-1 h-4 w-4" />{direction === "OUT" ? "Fattura manuale" : "Registra fattura"}</Button>
        </div>}
      </div>
      {shown.length === 0 ? <Empty text="Nessun documento" testId={`invoices-${p}-empty`} /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>{direction === "OUT" ? "Cliente" : "Fornitore"}</TableHead><TableHead>Società</TableHead><TableHead className="text-right">Imponibile</TableHead><TableHead className="text-right">Totale</TableHead>{direction === "OUT" && <TableHead>SDI</TableHead>}<TableHead>Pagamento</TableHead></TableRow></TableHeader>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setOpenId(r.id)} data-testid={`invoice-row-${r.id}`}>
                  <TableCell className="font-mono text-sm font-bold">{r.number || <span className="text-muted-foreground">Bozza</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-xs">{DOC[r.doc_type]}{r.reverse_charge ? " · RC" : ""}</TableCell>
                  <TableCell className="text-sm">{r.party_name}</TableCell>
                  <TableCell className="text-xs">{nameOf("companies", r.company_id, "code")}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{r.doc_type === "TD04" ? "−" : ""}{euro(r.taxable)}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold">{r.doc_type === "TD04" ? "−" : ""}{euro(r.total)}</TableCell>
                  {direction === "OUT" && <TableCell>{r.sdi_status ? <Chip map={SDI} value={r.sdi_status} /> : "—"}</TableCell>}
                  <TableCell><Chip map={PAY} value={r.payment_status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <InvoiceEditor open={!!editor} onClose={() => setEditor(null)} direction={direction} invoice={editor?.invoice} onSaved={load} />
      <FromProductionDialog open={fromProd} onClose={() => setFromProd(false)} onCreated={(inv) => { load(); setOpenId(inv.id); }} />
      <XmlImport open={xml} onClose={() => setXml(false)} onDone={load} />
      <InvoiceSheet id={openId} onClose={() => setOpenId(null)} onChanged={load} onOpen={setOpenId} onEdit={(inv) => { setOpenId(null); setEditor({ invoice: inv }); }} />
    </div>
  );
};
